import {
  Component,
  effect,
  EventEmitter,
  inject,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {AuthorizeVisibilityDirective, EsvgFiles, IFilter} from 'frontier/nucleus';
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from "@angular/material/tabs";
import {debounceTime, Subscription} from "rxjs";
import {ConspicuityDetailComponent} from './conspicuity-detail/conspicuity-detail.component';
import {ProcessOverviewStore} from '../service/process-overview.store';
import {CalculusStore} from '../../../../Services/calculus.store';
import {IReplacedClientForCartEvent} from '../service/event.interface';
import {PermanentOrdinanceComponent} from './permanent-ordinance/permanent-ordinance.component';
import {InternalNoteDetailComponent} from './internal-note-detail/internal-note-detail.component';
import {InvoiceDetailComponent} from './invoice-detail/invoice-detail.component';
import {CartAdditionalDetailComponent} from './cart-additional-detail/cart-additional-detail.component';
import {CrewDetailComponent} from './crew-detail/crew-detail.component';
import {TransportDetailComponent} from './transport-detail/transport-detail.component';
import {ClientDetailComponent} from './client-detail/client-detail.component';
import {ResizedDirective} from '../../../../../../projects/frontier/browserkit/src/lib/directives/resize.directive';
import {ICart, IClient} from '../service/state.interface';
import {AUTHORIZATION_CONFIG} from '../../../../config/authorization.config';
import {TabActivatedDirective} from "frontier/browserkit/src/lib/directives/tab-activated.directive";
import {MatIcon} from "@angular/material/icon";
import {ShippingAndPaymentComponent} from "../../shipping-and-payment/shipping-and-payment.component";
import {DocumentDetailComponent} from "./document-detail/document-detail.component";
import {IPdfOverlayToggleEvent} from "../carts/pdf-overlay-toggle-event.interface";
import {ChangeLogComponent} from "../../change-log/change-log.component";

@Component({
  selector: 'kpi4me-cart-details',
  templateUrl: './cart-details.component.html',
  styleUrls: ['./cart-details.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [
    MatTabGroup,
    ResizedDirective,
    MatTab,
    MatTabLabel,
    MatTabContent,
    ConspicuityDetailComponent,
    ClientDetailComponent,
    TransportDetailComponent,
    CrewDetailComponent,
    CartAdditionalDetailComponent,
    InvoiceDetailComponent,
    InternalNoteDetailComponent,
    PermanentOrdinanceComponent,
    AuthorizeVisibilityDirective,
    TabActivatedDirective,
    MatIcon,
    ShippingAndPaymentComponent,
    DocumentDetailComponent,
    ChangeLogComponent,
  ]
})
export class CartDetailsComponent implements OnInit, OnDestroy {
  protected readonly AUTHORIZATION_CONFIG = AUTHORIZATION_CONFIG;

  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _calculusService: CalculusStore = inject(CalculusStore);
  private _sizeChanged: EventEmitter<void> = new EventEmitter();
  calculusStore: CalculusStore = inject(CalculusStore);
  selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  selectedClient: Signal<IClient> = this._processOverviewService.selectedClient;

  internalNote: Signal<string> = this._processOverviewService.internalNote;
  selectedTabIndex: Signal<number> = this._processOverviewService.detailsTabIndex;
  showImportedValues: Signal<boolean> = this._calculusService.showImportedValues;

  cartFilter: ModelSignal<IFilter> = model<IFilter>(null);
  clientFilter: ModelSignal<IFilter> = model<IFilter>(null);

  tabRef: Signal<MatTabGroup> = viewChild('tabGroup');
  conspicuityDetail: Signal<ConspicuityDetailComponent> = viewChild(ConspicuityDetailComponent);
  @Output() detailDocumentToggleTriggered: EventEmitter<IPdfOverlayToggleEvent>
    = new EventEmitter<IPdfOverlayToggleEvent>();

  subs: Subscription = new Subscription();

  constructor() {
    effect(() => {
      const parent: ICart = this.selectedCart();
      if (parent) {
        this.cartFilter.update((filter: IFilter) => {
          return {
            ...filter,
            parent: parent.obj,
            typename: 'cart',
          }
        })
      }
    }, {allowSignalWrites: true});

    effect(() => {
      const parent: IClient = this.selectedClient();
      if (parent) {
        this.clientFilter.update((filter: IFilter) => {
          return {
            ...filter,
            parent: parent.obj,
            typename: 'client',
          }
        })
      }
    }, {allowSignalWrites: true});
  }

  ngOnInit(): void {
    this.subs.add(
      this._sizeChanged.pipe(
        debounceTime(300),
      ).subscribe(() => this.tabRef().updatePagination()),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  updatePagination(): void {
    this._sizeChanged.emit();
  }

  onClientReplaced(event: IReplacedClientForCartEvent): void {
    this._processOverviewService.replacedClientForCart$.next(event);
  }

  onSelectedIndexChange(evt: number): void {
    this._processOverviewService.detailsTabIndex$.next(evt);
  }

  protected readonly EsvgFiles = EsvgFiles;

  showHelpForDetails(): void {
    this.calculusStore.helpRoute$.next('prozessübersicht.html#detailansicht');
  }
}
